import { useCallback, useEffect, useRef, useState } from 'react'
import { message } from 'antd'
import { detectPlanManagementDataType } from '../api/detect-data-type-api'
import { type SurecoCsvDataType } from '../constants'
import { readCsvHeaderLine } from '../utils/readCsvHeaderLine'

export type CsvDetectPhase = 'idle' | 'detecting' | 'columnErrors' | 'noMatch'

export interface UseCsvDataTypeDetectionResult {
  phase: CsvDetectPhase
  file: File | null
  setFile: (file: File | null) => void
  detectedDataType: SurecoCsvDataType | null
  columnErrors: string[]
  /** Reads the file's header row and detects its data type. No-op without a file. */
  detect: () => Promise<void>
  /** Back to the empty dropzone — used both for a full modal reset and for
   * "Continue"/"Try again" after a detection failure. */
  reset: () => void
}

// Shared by every upload modal that auto-detects a CSV's data type from its
// header row instead of asking the user to pick one (Health Plan, Compare
// Data). Owns the file/phase state and the read-header-then-detect call —
// the part that's identical across features. What happens after a confident
// match (onConfidentMatch) is left to the caller, since that's where the
// features genuinely diverge: Health Plan shows a confirm screen and tracks
// a real import job, Compare Data proceeds immediately.
export function useCsvDataTypeDetection(
  onConfidentMatch: (dataType: SurecoCsvDataType, file: File) => void
): UseCsvDataTypeDetectionResult {
  const [phase, setPhase] = useState<CsvDetectPhase>('idle')
  const [file, setFile] = useState<File | null>(null)
  const [detectedDataType, setDetectedDataType] =
    useState<SurecoCsvDataType | null>(null)
  const [columnErrors, setColumnErrors] = useState<string[]>([])
  // Bumped by reset(), detect(), and unmount — a stale request only acts if its own id still matches.
  const generationRef = useRef(0)

  useEffect(() => {
    return () => {
      generationRef.current += 1
    }
  }, [])

  const reset = useCallback(() => {
    generationRef.current += 1
    setPhase('idle')
    setFile(null)
    setDetectedDataType(null)
    setColumnErrors([])
  }, [])

  const detect = useCallback(async () => {
    if (!file) return
    const generation = ++generationRef.current
    setPhase('detecting')
    try {
      const headerLine = await readCsvHeaderLine(file)
      if (generation !== generationRef.current) return
      const detection = await detectPlanManagementDataType(headerLine)
      if (generation !== generationRef.current) return

      if (!detection.matched || !detection.dataType) {
        setPhase('noMatch')
        return
      }

      // Set for both branches below — a confident match needs it too
      // (e.g. Health Plan's confirm screen reads it from this hook).
      setDetectedDataType(detection.dataType)

      if (!detection.success) {
        setColumnErrors(detection.columnErrors)
        setPhase('columnErrors')
        return
      }

      onConfidentMatch(detection.dataType, file)
    } catch (err) {
      if (generation !== generationRef.current) return
      // eslint-disable-next-line no-console
      console.error('Failed to detect data type', err)
      message.error('Could not read the file. Please try again.')
      setPhase('idle')
    }
  }, [file, onConfidentMatch])

  return { phase, file, setFile, detectedDataType, columnErrors, detect, reset }
}
